// ============================================================
// BLACK OWL - House Rules wording (FB-24 to FB-27)
// ============================================================
// The host says what arrows mean in real life:
//   Arrow      = 1 arrow, "push-up"
//   Cannonball = N arrows, "lap"          (optional)
//   Meteor     = M arrows, "10 burpees"   (optional)
// Unit names are fixed (D1). Amounts are always computed on the
// server; these helpers only build the words, so every screen says
// the same thing. With no rules set, wording is unchanged.
// ============================================================

export const PACKAGE_UNITS = ["cannonball", "meteor"];
export const UNIT_TITLES = { arrow: "Arrow", cannonball: "Cannonball", meteor: "Meteor" };
export const RULE_TEXT_MAX = 40;
export const RULE_WORTH_MIN = 2;
export const RULE_WORTH_MAX = 500;

// The enabled packages, smallest first: [{ unit, worth, action }]
export function packageOptions(rules) {
  return PACKAGE_UNITS
    .filter((unit) => rules?.[unit]?.worth > 0)
    .map((unit) => ({ unit, ...rules[unit] }))
    .sort((a, b) => a.worth - b.worth);
}

export function hasHouseRules(rules) {
  return Boolean(rules?.arrow?.action || packageOptions(rules).length);
}

// A one-word action counts like a noun: "3 push-ups", "1 lap", "2 laps".
// A phrase ("run a lap") or one that starts with a number ("10 burpees")
// reads as "1 × run a lap", "2 × 10 burpees" instead of "1 run a lap".
// A custom plural means the host wrote a noun, so it always counts.
export function servePhrase(rule, count) {
  const action = rule?.action;
  if (!action) return "";
  if (!rule.plural && (/\s/.test(action) || /^\d/.test(action))) return `${count} × ${action}`;
  return `${count} ${count === 1 ? action : (rule.plural || `${action}s`)}`;
}

// What a shot is, plus what it means in real life.
//   arrows:  { what: "3 arrows",                     serve: "3 push-ups" }
//   package: { what: "a CANNONBALL (10 arrows)",     serve: "1 lap" }
// `rule` overrides the lobby rule (ledger items keep the action text they
// were fired with). Penalties owed to The Game are always loose arrows (D10).
export function describePenalty(rules, unit, count, arrows, rule) {
  const isPackage = PACKAGE_UNITS.includes(unit);
  if (!isPackage) {
    const n = arrows ?? count ?? 0;
    return { what: `${n} arrow${n !== 1 ? "s" : ""}`, serve: servePhrase(rule || rules?.arrow, n) };
  }
  const name = UNIT_TITLES[unit].toUpperCase();
  const shots = count || 1;
  const what = shots === 1
    ? `a ${name} (${arrows} arrows)`
    : `${shots} ${name}S (${arrows} arrows)`;
  return { what, serve: servePhrase(rule || rules?.[unit], shots) };
}
